import { defineStore } from "pinia";
import axios from "axios";
import { useStore } from "@/store/index";
import { t } from "@/controllerLK/GlobusLocalization";

const REPORTS_API_BASE = "/subscriptions";

/**
 * Маппинг значимости из внутреннего формата в API формат
 */
export const importanceMapping = {
  interesting: 1,
  moderate: 2,
  important: 3,
  critical: 4
};

/**
 * Маппинг значимости из API формата во внутренний формат
 */
export const importanceMappingReverse = {
  normal: ["interesting"],
  gently_important: ["moderate"],
  important: ["important"],
  critical: ["critical"]
};

/**
 * Маппинг тональности (внутренний формат совпадает с API)
 */
export const sentimentValues = ["positive", "neutral", "negative"];

/**
 * Преобразование периода во внутренний формат частоты
 * Формат TimeSpan: "d.hh:mm:ss" или "hh:mm:ss"
 * Примеры: "7.00:00:00" (7 дней), "03:00:00" (3 часа), "00:20:00" (20 минут)
 * @param {string} period - Период из API (формат TimeSpan .NET)
 * @returns {string} Внутренний формат частоты
 */
export function periodToFrequency(period) {
  const mapping = {
    "immediate": "immediately",
    "every_3_hours": "every_3h",
    "every_6_hours": "every_6h",
    "every_24_hours": "every_24h",
    "weekly": "weekly"
  };

  return mapping[period] || "immediately";
}

/**
 * Преобразование внутреннего формата частоты в период API
 * @param {string} frequency - Внутренний формат частоты
 * @returns {string} Период в формате TimeSpan .NET
 */
export function frequencyToPeriod(frequency) {
  const mapping = {
    immediately: "immediate",
    every_3h: "every_3_hours",
    every_6h: "every_6_hours",
    every_24h: "every_24_hours",
    weekly: "weekly"
  };

  return mapping[frequency] || "00:01:00";
}

/**
 * Pinia store для работы с подписками на Telegram-уведомления
 * @see https://dev.ms-reports.globus.guru/index.html
 */
export const useSubscriptions = defineStore({
  id: "subscriptionsStore",
  state: () => ({
    subscriptions: [],
    currentSubscription: null,
    loading: false
  }),
  actions: {
    /**
     * Получить список подписок
     * @param {Object} params - Параметры запроса
     * @param {number} [params.page] - Номер страницы (минимум 1)
     * @param {number} [params.limit=100] - Лимит записей на странице (минимум 1, по умолчанию 100)
     * @returns {Promise<Array>} Список подписок
     */
    async getSubscriptions(params = {}) {
      const mainStore = useStore();
      this.loading = true;

      try {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append("page", params.page);
        if (params.limit) queryParams.append("limit", params.limit);

        const url = `${REPORTS_API_BASE}/subscriptions?page=1&limit=100`;
        const response = await axios.get(url);
        this.subscriptions = response.data;
        return response.data;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.get_list_error"), "error");
        console.error(e);
        return [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Получить подписку по ID
     * @param {string} subscriptionId - UUID подписки
     * @returns {Promise<Object|null>} Данные подписки
     */
    async getSubscription(subscriptionId) {
      const mainStore = useStore();

      try {
        const response = await axios.get(`${REPORTS_API_BASE}/subscriptions/${subscriptionId}`);
        this.currentSubscription = response.data;
        return response.data;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.get_error"), "error");
        console.error(e);
        return null;
      }
    },

    /**
     * Создать новую подписку
     * @param {Object} data - Данные подписки
     * @param {string} [data.project_id] - ID проекта
     * @param {number[]} [data.rubric_ids] - Массив ID рубрик (тем)
     * @param {string[]} [data.importance] - Массив уровней значимости ('normal', 'important', 'critical')
     * @param {string[]} [data.sentiment] - Массив тональностей ('neutral', 'positive', 'negative')
     * @param {string} data.period - Период отправки (формат date-span)
     * @returns {Promise<Object|null>} Созданная подписка
     */
    async createSubscription(data) {
      const mainStore = useStore();

      try {
        const response = await axios.post(`${REPORTS_API_BASE}/subscriptions`, data);
        this.subscriptions.push(response.data);
        mainStore.createNotify(t("notification.subscriptions.create_success"), "success");
        return response.data;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.create_error"), "error");
        console.error(e);
        return null;
      }
    },

    /**
     * Полное обновление подписки (PUT)
     * @param {string} subscriptionId - UUID подписки
     * @param {Object} data - Данные для обновления
     * @param {string} [data.project_id] - ID проекта
     * @param {number[]} [data.rubric_ids] - Массив ID рубрик (тем)
     * @param {string[]} [data.importance] - Массив уровней значимости
     * @param {string[]} [data.sentiment] - Массив тональностей
     * @param {string} data.period - Период отправки
     * @returns {Promise<Object|null>} Обновлённая подписка
     */
    async updateSubscription(subscriptionId, data) {
      const mainStore = useStore();

      try {
        const response = await axios.put(`${REPORTS_API_BASE}/subscriptions/${subscriptionId}`, data);
        const index = this.subscriptions.findIndex(s => s.id === subscriptionId);
        if (index !== -1) {
          this.subscriptions[index] = response.data;
        }
        mainStore.createNotify(t("notification.subscriptions.update_success"), "success");
        return response.data;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.update_error"), "error");
        console.error(e);
        return null;
      }
    },

    /**
     * Частичное обновление подписки (PATCH)
     * @param {string} subscriptionId - UUID подписки
     * @param {Object} data - Данные для частичного обновления
     * @param {string} [data.project_id] - ID проекта
     * @param {number[]} [data.rubric_ids] - Массив ID рубрик (тем)
     * @param {string[]} [data.importance] - Массив уровней значимости
     * @param {string[]} [data.sentiment] - Массив тональностей
     * @param {string} [data.period] - Период отправки
     * @returns {Promise<Object|null>} Обновлённая подписка
     */
    async patchSubscription(subscriptionId, data) {
      const mainStore = useStore();

      try {
        const response = await axios.patch(`${REPORTS_API_BASE}/subscriptions/${subscriptionId}`, data);
        const index = this.subscriptions.findIndex(s => s.id === subscriptionId);
        if (index !== -1) {
          this.subscriptions[index] = response.data;
        }
        mainStore.createNotify(t("notification.subscriptions.update_success"), "success");
        return response.data;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.update_error"), "error");
        console.error(e);
        return null;
      }
    },

    /**
     * Удалить подписку
     * @param {string} subscriptionId - UUID подписки
     * @returns {Promise<boolean>} Успешность удаления
     */
    async deleteSubscription(subscriptionId) {
      const mainStore = useStore();

      try {
        await axios.delete(`${REPORTS_API_BASE}/subscriptions/${subscriptionId}`);
        this.subscriptions = this.subscriptions.filter(s => s.id !== subscriptionId);
        mainStore.createNotify(t("notification.subscriptions.delete_success"), "success");
        return true;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.delete_error"), "error");
        console.error(e);
        return false;
      }
    },

    /**
     * Отвязать чат пользователя от Telegram-бота
     * @returns {Promise<boolean>} Успешность отвязки
     */
    async unlinkTelegramChat() {
      const mainStore = useStore();

      try {
        await axios.post(`${REPORTS_API_BASE}/users/profile/chat-untying`);
        mainStore.createNotify(t("notification.subscriptions.unlink_success"), "success");
        return true;
      } catch (e) {
        mainStore.createNotify(t("notification.subscriptions.unlink_error"), "error");
        console.error(e);
        return false;
      }
    },

    /**
     * Очистить текущую подписку
     */
    clearCurrentSubscription() {
      this.currentSubscription = null;
    },

    /**
     * Очистить все подписки
     */
    clearSubscriptions() {
      this.subscriptions = [];
      this.currentSubscription = null;
    }
  }
});

export default useSubscriptions;
